// src/gestion-logistics/hooks/useLogisticsPermissions.ts
import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";

export interface LogisticsPermissions {
  // Module access
  canViewModule: boolean;

  // Shipments
  canViewShipments: boolean;
  canCreateShipments: boolean;
  canUpdateShipmentStatus: boolean;
  canDeleteShipments: boolean;

  // Job Costing
  canViewCosting: boolean;
  canEditCosting: boolean;

  // Account Masters
  canViewAccountMasters: boolean;
  canManageAccountMasters: boolean;
  canCreateAccountMasters: boolean;
  canEditAccountMasters: boolean;
  canDeleteAccountMasters: boolean;

  // Invoices
  canViewInvoices: boolean;
  canCreateInvoices: boolean;
  canGenerateInvoices: boolean;
  canDeleteInvoices: boolean;

  // Customer Payments
  canViewCustomerPayments: boolean;
  canCreateCustomerPayments: boolean;
  canEditCustomerPayments: boolean;
  canDeleteCustomerPayments: boolean;

  // Supplier Bills
  canViewSupplierBills: boolean;
  canCreateSupplierBills: boolean;

  // Supplier Payments
  canViewSupplierPayments: boolean;
  canCreateSupplierPayments: boolean;
  canEditSupplierPayments: boolean;
  canDeleteSupplierPayments: boolean;

  // Dashboard and Reports
  canViewDashboard: boolean;
  canViewReports: boolean;
  canExportData: boolean;

  // Treasury
  canViewTreasury: boolean;
  canExportTreasury: boolean;
  canViewTreasuryAccounts: boolean;
  canCreateTreasuryAccounts: boolean;
  canEditTreasuryAccounts: boolean;
  canDeleteTreasuryAccounts: boolean;
  canViewTreasuryMovements: boolean;
  canCreateTreasuryMovements: boolean;
  canCreateTreasuryTransfers: boolean;

  // Expenses
  canViewExpenses: boolean;
  canCreateExpenses: boolean;
  canEditExpenses: boolean;
  canDeleteExpenses: boolean;
  canExportExpenses: boolean;
}

/**
 * Custom hook pour gérer les permissions du module Logistics
 * Utilise le contexte AuthContext pour vérifier les permissions de l'utilisateur
 */
export const useLogisticsPermissions = (): LogisticsPermissions => {
  const auth = useAuth();

  const permissions = useMemo<LogisticsPermissions>(() => {
    // Si le contexte d'authentification n'est pas disponible, ne donner aucune permission
    if (!auth) {
      return {
        canViewModule: false,
        canViewShipments: false,
        canCreateShipments: false,
        canUpdateShipmentStatus: false,
        canDeleteShipments: false,
        canViewCosting: false,
        canEditCosting: false,
        canViewAccountMasters: false,
        canManageAccountMasters: false,
        canCreateAccountMasters: false,
        canEditAccountMasters: false,
        canDeleteAccountMasters: false,
        canViewInvoices: false,
        canCreateInvoices: false,
        canGenerateInvoices: false,
        canDeleteInvoices: false,
        canViewCustomerPayments: false,
        canCreateCustomerPayments: false,
        canEditCustomerPayments: false,
        canDeleteCustomerPayments: false,
        canViewSupplierBills: false,
        canCreateSupplierBills: false,
        canViewSupplierPayments: false,
        canCreateSupplierPayments: false,
        canEditSupplierPayments: false,
        canDeleteSupplierPayments: false,
        canViewDashboard: false,
        canViewReports: false,
        canExportData: false,
        canViewTreasury: false,
        canExportTreasury: false,
        canViewTreasuryAccounts: false,
        canCreateTreasuryAccounts: false,
        canEditTreasuryAccounts: false,
        canDeleteTreasuryAccounts: false,
        canViewTreasuryMovements: false,
        canCreateTreasuryMovements: false,
        canCreateTreasuryTransfers: false,
        canViewExpenses: false,
        canCreateExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,
        canExportExpenses: false,
      };
    }
    
    // Si l'utilisateur est superadmin, il a toutes les permissions
    if (auth.user?.is_superadmin) {
      return {
        canViewModule: true,
        canViewShipments: true,
        canCreateShipments: true,
        canUpdateShipmentStatus: true,
        canDeleteShipments: true,
        canViewCosting: true,
        canEditCosting: true,
        canViewAccountMasters: true,
        canManageAccountMasters: true,
        canCreateAccountMasters: true,
        canEditAccountMasters: true,
        canDeleteAccountMasters: true,
        canViewInvoices: true,
        canCreateInvoices: true,
        canGenerateInvoices: true,
        canDeleteInvoices: true,
        canViewCustomerPayments: true,
        canCreateCustomerPayments: true,
        canEditCustomerPayments: true,
        canDeleteCustomerPayments: true,
        canViewSupplierBills: true,
        canCreateSupplierBills: true,
        canViewSupplierPayments: true,
        canCreateSupplierPayments: true,
        canEditSupplierPayments: true,
        canDeleteSupplierPayments: true,
        canViewDashboard: true,
        canViewReports: true,
        canExportData: true,
        canViewTreasury: true,
        canExportTreasury: true,
        canViewTreasuryAccounts: true,
        canCreateTreasuryAccounts: true,
        canEditTreasuryAccounts: true,
        canDeleteTreasuryAccounts: true,
        canViewTreasuryMovements: true,
        canCreateTreasuryMovements: true,
        canCreateTreasuryTransfers: true,
        canViewExpenses: true,
        canCreateExpenses: true,
        canEditExpenses: true,
        canDeleteExpenses: true,
        canExportExpenses: true,
      };
    }

    // Vérifier les permissions individuelles
    const hasPermission = (key: string): boolean => {
      return auth.hasPermission?.(key) || false;
    };

    return {
      // Module access
      canViewModule: hasPermission("logistics.view"),

      // Shipments
      canViewShipments: hasPermission("logistics.shipments.view"),
      canCreateShipments: hasPermission("logistics.shipments.create"),
      canUpdateShipmentStatus: hasPermission("logistics.shipments.edit"),
      canDeleteShipments: hasPermission("logistics.shipments.delete"),

      // Job Costing
      canViewCosting: hasPermission("logistics.costing.view"),
      canEditCosting: hasPermission("logistics.costing.edit"),

      // Account Masters
      canViewAccountMasters: hasPermission("logistics.account_masters.view"),
      canManageAccountMasters: hasPermission("logistics.account_masters.manage"),
      canCreateAccountMasters: hasPermission("logistics.account_masters.create"),
      canEditAccountMasters: hasPermission("logistics.account_masters.edit"),
      canDeleteAccountMasters: hasPermission("logistics.account_masters.delete"),

      // Invoices
      canViewInvoices: hasPermission("logistics.invoices.view"),
      canCreateInvoices: hasPermission("logistics.invoices.create"),
      canGenerateInvoices: hasPermission("logistics.invoices.generate"),
      canDeleteInvoices: hasPermission("logistics.invoices.delete"),

      // Customer Payments
      canViewCustomerPayments: hasPermission("logistics.customer_payments.view"),
      canCreateCustomerPayments: hasPermission("logistics.customer_payments.create"),
      canEditCustomerPayments: hasPermission("logistics.customer_payments.edit"),
      canDeleteCustomerPayments: hasPermission("logistics.customer_payments.delete"),

      // Supplier Bills
      canViewSupplierBills: hasPermission("logistics.supplier_bills.view"),
      canCreateSupplierBills: hasPermission("logistics.supplier_bills.create"),

      // Supplier Payments
      canViewSupplierPayments: hasPermission("logistics.supplier_payments.view"),
      canCreateSupplierPayments: hasPermission("logistics.supplier_payments.create"),
      canEditSupplierPayments: hasPermission("logistics.supplier_payments.edit"),
      canDeleteSupplierPayments: hasPermission("logistics.supplier_payments.delete"),

      // Dashboard and Reports
      canViewDashboard: hasPermission("logistics.dashboard.view"),
      canViewReports: hasPermission("logistics.reports.view"),
      canExportData: hasPermission("logistics.reports.export"),

      // Treasury
      canViewTreasury: hasPermission("logistics.treasury.view"),
      canExportTreasury: hasPermission("logistics.treasury.export"),
      canViewTreasuryAccounts: hasPermission("logistics.treasury.accounts.view"),
      canCreateTreasuryAccounts: hasPermission(
        "logistics.treasury.accounts.create"
      ),
      canEditTreasuryAccounts: hasPermission("logistics.treasury.accounts.edit"),
      canDeleteTreasuryAccounts: hasPermission(
        "logistics.treasury.accounts.delete"
      ),
      canViewTreasuryMovements: hasPermission(
        "logistics.treasury.movements.view"
      ),
      canCreateTreasuryMovements: hasPermission(
        "logistics.treasury.movements.create"
      ),
      canCreateTreasuryTransfers: hasPermission(
        "logistics.treasury.transfers.create"
      ),

      // Expenses
      canViewExpenses: hasPermission("logistics.expenses.entries.view"),
      canCreateExpenses: hasPermission("logistics.expenses.entries.create"),
      canEditExpenses: hasPermission("logistics.expenses.entries.edit"),
      canDeleteExpenses: hasPermission("logistics.expenses.entries.delete"),
      canExportExpenses: hasPermission("logistics.expenses.entries.export"),
    };
  }, [auth]);

  return permissions;
};
